import { isHomeTeamEvent } from "./EventUtils";
import { getVSSMatchSide } from "./VSSReports";

export const MAX_VSS_FILE_BYTES = 5 * 1024 * 1024;
const MAX_CELLS = 50000;
const MAX_ROWS = 500;
const MAX_COLUMNS = 200;
const MAX_STEPS = 2000000;
const FUNCTIONS = new Set([
  "IF", "IFERROR", "SUM", "COUNT", "COUNTA", "AVERAGE", "MIN", "MAX",
  "ROUND", "ROUNDUP", "ABS", "LEN", "LEFT", "RIGHT", "CONCATENATE", "AND", "OR", "NOT",
  "VSPLAYER", "VSPLAYERNAME", "VSCELL", "VSCOUNT", "VSPER", "VSEFF",
  "VSPLAYS", "VSMAP", "VSRANGE", "VSOR", "VSAND",
]);
const SKILLS = { 1: "S", 2: "R", 3: "E", 4: "A", 5: "B", 6: "D", 7: "F", 20: "E" };
const POSITIONS = { libero: 1, outside: 2, opposite: 3, middle: 4, setter: 5 };
const ARITY = {
  IF: [2, 3], IFERROR: [2, 2], NOT: [1, 1], ABS: [1, 1], ROUND: [1, 2], ROUNDUP: [2, 2],
  LEN: [1, 1], LEFT: [1, 2], RIGHT: [1, 2], VSPLAYER: [1, 2],
  VSPLAYERNAME: [1, 1], VSCELL: [2, 20], VSCOUNT: [1, 20],
  VSPER: [1, 20], VSEFF: [1, 20], VSPLAYS: [1, 2],
  VSMAP: [2, 2], VSRANGE: [1, 1],
};

class FormulaError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

class NoDataRatioError extends FormulaError {
  constructor() {
    super("#DIV/0!", "Ratio has a zero denominator.");
  }
}

function fail(message, code = "#VALUE!") {
  throw new FormulaError(code, message);
}

export function vssColumnName(column) {
  let name = "";
  for (let n = column; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + (n - 1) % 26) + name;
  }
  return name;
}

function coordinates(reference) {
  const match = /^\$?([A-Z]+)\$?([1-9]\d*)$/i.exec(reference);
  if (!match) fail(`Invalid cell reference: ${reference}`, "#REF!");
  const column = [...match[1].toUpperCase()].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0);
  const row = Number(match[2]);
  if (row > MAX_ROWS || column > MAX_COLUMNS) fail("Cell reference exceeds supported sheet size.", "#REF!");
  return { row, column };
}

export function parseVSSWorkbook(text) {
  if (typeof text !== "string" || new Blob([text]).size > MAX_VSS_FILE_BYTES) {
    throw new Error("VSS templates must be text files smaller than 5 MB.");
  }
  let workbook;
  try {
    workbook = JSON.parse(text.replace(/^\uFEFF/, ""));
  } catch (error) {
    throw new Error("This is not a valid JSON VSS workbook.");
  }
  if (!Array.isArray(workbook) || !workbook.length || workbook.length > 10) {
    throw new Error("A VSS workbook must contain between 1 and 10 sheets.");
  }
  let count = 0;
  const names = new Set();
  for (const sheet of workbook) {
    if (!sheet || typeof sheet.name !== "string" || !sheet.name.trim() ||
      names.has(sheet.name.toLowerCase()) || !Array.isArray(sheet.content?.data)) {
      throw new Error("Each VSS sheet needs a unique name and a content.data cell array.");
    }
    names.add(sheet.name.toLowerCase());
    const addresses = new Set();
    for (const cell of sheet.content.data) {
      if (!Array.isArray(cell) || !Number.isInteger(cell[0]) || !Number.isInteger(cell[1]) ||
        cell[0] < 1 || cell[0] > MAX_ROWS || cell[1] < 1 || cell[1] > MAX_COLUMNS ||
        (!["string", "number", "boolean"].includes(typeof cell[2]) && cell[2] != null) ||
        (typeof cell[2] === "number" && !Number.isFinite(cell[2])) ||
        (typeof cell[2] === "string" && cell[2].length > 8192)) {
        throw new Error(`Invalid cell in sheet "${sheet.name}" (maximum size: 500 rows by 200 columns).`);
      }
      const address = `${cell[0]}:${cell[1]}`;
      if (addresses.has(address)) throw new Error(`Duplicate cell ${address} in "${sheet.name}".`);
      addresses.add(address);
      count += 1;
    }
    if (count > MAX_CELLS) throw new Error("VSS templates may contain at most 50,000 cells.");
    for (const key of ["styles", "sizes", "spans", "conditions", "views"]) {
      if (sheet.content[key] != null && !Array.isArray(sheet.content[key])) {
        throw new Error(`Invalid ${key} array in sheet "${sheet.name}".`);
      }
    }
    if ((sheet.content.styles ?? []).some((style) =>
      !Array.isArray(style) || typeof style[0] !== "string" || typeof style[1] !== "string") ||
      (sheet.content.sizes ?? []).some((size) =>
        !Array.isArray(size) || !size.slice(0, 3).every(Number.isFinite) || size.length < 3) ||
      (sheet.content.conditions ?? []).some((condition) => !Array.isArray(condition))) {
      throw new Error(`Invalid layout metadata in sheet "${sheet.name}".`);
    }
    const mergedCells = new Set();
    let mergedArea = 0;
    for (const span of sheet.content.spans ?? []) {
      if (!Array.isArray(span) || span.length < 4 ||
        !span.slice(0, 4).every((n) => Number.isInteger(n) && n > 0) ||
        span[0] + span[3] - 1 > MAX_ROWS || span[1] + span[2] - 1 > MAX_COLUMNS) {
        throw new Error(`Invalid merged cells in sheet "${sheet.name}".`);
      }
      mergedArea += span[2] * span[3];
      if (mergedArea > MAX_CELLS) throw new Error(`Too many merged cells in sheet "${sheet.name}".`);
      for (let row = span[0]; row < span[0] + span[3]; row += 1) {
        for (let column = span[1]; column < span[1] + span[2]; column += 1) {
          const address = `${row}:${column}`;
          if (mergedCells.has(address)) throw new Error(`Overlapping merged cells in sheet "${sheet.name}".`);
          mergedCells.add(address);
        }
      }
    }
  }
  return workbook;
}

function tokenize(formula) {
  const tokens = [];
  let index = 1;
  while (index < formula.length) {
    if (/\s/.test(formula[index])) { index += 1; continue; }
    const rest = formula.slice(index);
    const match = /^(?:"((?:[^"]|"")*)"|'((?:[^']|'')*)'|(\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|\.\d+)|(\$?[A-Za-z_][A-Za-z_0-9.$]*)|(<=|>=|<>|[+\-*/^&=<>%(),;:!]))/.exec(rest);
    if (!match) fail(`Unsupported formula syntax near "${rest.slice(0, 20)}".`);
    index += match[0].length;
    if (match[1] !== undefined) tokens.push({ type: "literal", value: match[1].replace(/""/g, '"') });
    else if (match[2] !== undefined) tokens.push({ type: "sheet", value: match[2].replace(/''/g, "'") });
    else if (match[3] !== undefined) tokens.push({ type: "literal", value: Number(match[3]) });
    else if (match[4] !== undefined) tokens.push({ type: "name", value: match[4] });
    else tokens.push({ type: match[5] === ";" ? "," : match[5], value: match[5] });
  }
  if (tokens.length > 2000) fail("Formula is too complex.");
  tokens.push({ type: "end" });
  return tokens;
}

function parseFormula(formula) {
  const tokens = tokenize(formula);
  let index = 0;
  let depth = 0;
  const peek = () => tokens[index];
  const take = (type) => {
    if (peek().type !== type) fail(`Expected ${type} in formula.`);
    return tokens[index++];
  };
  const precedence = { "=": 1, "<>": 1, "<": 1, ">": 1, "<=": 1, ">=": 1, "&": 2, "+": 3, "-": 3, "*": 4, "/": 4, "^": 5 };
  function expression(minimum = 0) {
    depth += 1;
    if (depth > 100) fail("Formula nesting is too deep.");
    let node;
    const token = tokens[index++];
    if (token.type === "+" || token.type === "-") {
      node = { type: "unary", operator: token.type, value: expression(6) };
    } else if (token.type === "(") {
      node = expression();
      take(")");
    } else if (token.type === "literal") {
      node = { type: "literal", value: token.value };
    } else if (token.type === "name" || token.type === "sheet") {
      const name = token.value.toUpperCase();
      if (peek().type === "(" && token.type === "name") {
        if (!FUNCTIONS.has(name)) fail(`Unsupported function: ${name}.`, "#NAME?");
        take("(");
        const args = [];
        if (peek().type !== ")") {
          do {
            if (peek().type === ",") take(",");
            args.push(expression());
          } while (peek().type === ",");
        }
        take(")");
        const [minimum, maximum] = ARITY[name] ?? [1, 200];
        if (args.length < minimum || args.length > maximum) fail(`Invalid number of arguments for ${name}.`);
        node = { type: "call", name, args };
      } else if (peek().type === "!") {
        take("!");
        node = { type: "reference", sheet: token.value, reference: take("name").value };
        coordinates(node.reference);
      } else if (["TRUE", "FALSE"].includes(name)) {
        node = { type: "literal", value: name === "TRUE" };
      } else {
        coordinates(token.value);
        node = { type: "reference", reference: token.value };
      }
      if (node.type === "reference" && peek().type === ":") {
        take(":");
        node = { type: "range", start: node, end: coordinates(take("name").value) };
      }
    } else {
      fail("Expected a value in formula.");
    }
    while (peek().type === "%") {
      take("%");
      node = { type: "binary", operator: "/", left: node, right: { type: "literal", value: 100 } };
    }
    while (precedence[peek().type] !== undefined && precedence[peek().type] >= minimum) {
      const operator = tokens[index++].type;
      node = {
        type: "binary", operator, left: node,
        right: expression(precedence[operator] + (operator === "^" ? 0 : 1)),
      };
    }
    depth -= 1;
    return node;
  }
  const node = expression();
  take("end");
  return node;
}

function number(value) {
  if (value === "" || value == null) return 0;
  if (typeof value !== "number" && typeof value !== "string" && typeof value !== "boolean") {
    fail("Expected a number.");
  }
  const result = Number(value);
  if (!Number.isFinite(result)) fail("Expected a finite number.");
  return result;
}

function text(value) {
  if (value == null) return "";
  if (typeof value === "object") fail("Expected text, not a query or array.");
  return String(value);
}

function binary(operator, left, right) {
  if (operator === "&") {
    const a = text(left);
    const b = text(right);
    if (a.length + b.length > 32767) fail("Cell text limit exceeded.", "#LIMIT!");
    return a + b;
  }
  if (["=", "<>", "<", ">", "<=", ">="].includes(operator)) {
    const a = typeof left === "string" ? left.toLowerCase() : left;
    const b = typeof right === "string" ? right.toLowerCase() : right;
    if (operator === "=") return a === b;
    if (operator === "<>") return a !== b;
    if (operator === "<") return a < b;
    if (operator === ">") return a > b;
    if (operator === "<=") return a <= b;
    return a >= b;
  }
  const a = number(left);
  const b = number(right);
  if (operator === "+") return a + b;
  if (operator === "-") return a - b;
  if (operator === "*") return a * b;
  if (operator === "/") {
    if (b === 0) throw new NoDataRatioError();
    return a / b;
  }
  return a ** b;
}

function simpleQuery(value) {
  if (typeof value === "object" && value?.kind === "query") return value;
  const code = text(value);
  const [mask, ...modifiers] = code.split(";");
  const match = /^([*a])?(\d{1,2})?(.*)$/.exec(mask);
  const setting = /^E([A-Z][A-Z0-9])?([BCFP])?([#+!=/-])?$/.exec(match?.[3] ?? "");
  const basic = /^([SREABDF])?([#+!=/-])?$/.exec(match?.[3] ?? "");
  const attack = /^([A-Z][A-Z0-9])([#+!=/-])?$/.exec(match?.[3] ?? "");
  const attackCode = /^A([HMQTUF])?([#+!=/-])?([0-9])([0-9])([#+!=/-])?$/.exec(match?.[3] ?? "");
  const serveCode = /^S([1-9])$/.exec(match?.[3] ?? "");
  if (!match || (!setting && !basic && !attack && !attackCode && !serveCode) || !mask ||
    (attackCode?.[2] && attackCode?.[5]) ||
    (basic && !basic[1] && !match[1] && !match[2])) {
    fail(`Unsupported VolleyStation filter: "${code}".`);
  }
  const skill = setting ? "E" : basic ? basic[1] ?? null : serveCode ? "S" : "A";
  const effect = setting ? setting[3] : basic ? basic[2] : attackCode ? attackCode[2] ?? attackCode[5] : attack?.[2];
  let relation = null;
  let negate = false;
  const constraints = [];
  for (const modifier of modifiers.filter(Boolean)) {
    if (/^[*a]Z$/.test(modifier)) continue;
    const player = /^([*a])(\d{1,2})?$/.exec(modifier);
    if (player) {
      constraints.push({ type: "player", team: player[1], shirt: player[2] ? Number(player[2]) : null });
      continue;
    }
    if (modifier === "NOT" && !negate) {
      negate = true;
      continue;
    }
    const range = /^(PR|PA)(\*|-?\d+):(\*|-?\d+)$/.exec(modifier);
    if (range) {
      const min = range[2] === "*" ? null : Number(range[2]);
      const max = range[3] === "*" ? null : Number(range[3]);
      if ((min !== null && !Number.isSafeInteger(min)) ||
        (max !== null && !Number.isSafeInteger(max)) ||
        (min !== null && max !== null && min > max) ||
        (range[1] === "PR" && (min < 0 || max < 0))) {
        fail(`Invalid score range: "${modifier}".`);
      }
      if (min !== null || max !== null) constraints.push({ type: range[1], min, max });
      continue;
    }
    const rotation = /^([*a])Z([1-6]+)$/.exec(modifier);
    if (rotation) {
      constraints.push({ type: "rotation", team: rotation[1], positions: rotation[2] });
      continue;
    }
    const outcome = /^([WL])R-0$/.exec(modifier);
    if (outcome) {
      constraints.push({ type: "winner", team: outcome[1] === "W" ? "*" : "a" });
      continue;
    }
    const offset = /^([PBR])([+-]\d+)$/.exec(modifier);
    if (!offset || relation) fail(`Unsupported VolleyStation filter: "${code}".`);
    if (offset[1] === "R" && Number(offset[2]) !== 0) fail(`Unsupported VolleyStation filter: "${code}".`);
    relation = { unit: offset[1], offset: Number(offset[2]) };
    if (!Number.isSafeInteger(relation.offset)) fail(`Invalid play offset: "${modifier}".`);
  }
  return {
    kind: "query", operator: "code",
    team: match[1] ?? (relation ? null : "*"), shirt: match[2] ? Number(match[2]) : null,
    skill, effect: effect ?? null,
    setterCall: setting?.[1] ?? null, targetAttacker: setting?.[2] ?? null,
    combination: !setting && !basic && !attackCode && !serveCode ? attack[1] : null,
    hitType: attackCode?.[1] ?? null,
    startZone: serveCode ? serveCode[1] : attackCode && attackCode[3] !== "0" ? attackCode[3] : null,
    endZone: attackCode && attackCode[4] !== "0" ? attackCode[4] : null,
    relation, negate, constraints,
  };
}

function matchesCode(play, query, ignoreEffect, context) {
  if (!((!query.team || play.team === query.team) &&
    (query.shirt == null || play.player === query.shirt) &&
    (!query.skill || play.skill === query.skill) &&
    (ignoreEffect || !query.effect || play.effect === query.effect))) return false;
  for (const field of ["setterCall", "targetAttacker", "combination", "hitType", "startZone", "endZone"]) {
    if (!query[field]) continue;
    if (!play[field]) {
      const labels = {
        setterCall: "setter call", targetAttacker: "target attacker", combination: "attack combination",
        hitType: "hit type", startZone: "start zone", endZone: "end zone",
      };
      context.warnings.add(`Some matching plays have no recorded ${labels[field]}; those plays are excluded from filters requiring it.`);
      return null;
    }
    if (play[field] !== query[field]) return false;
  }
  for (const constraint of query.constraints) {
    let value;
    if (constraint.type === "player") {
      if (play.team !== constraint.team || (constraint.shirt !== null && play.player !== constraint.shirt)) return false;
    } else if (constraint.type === "winner") {
      const winner = context.rallies.get(play.rally)?.winner;
      if (!winner) {
        context.warnings.add("Some matching rallies have no recorded winner; those rallies are excluded from win/loss filters.");
        return null;
      }
      if (winner !== constraint.team) return false;
    } else if (constraint.type === "rotation") {
      value = play.rotations[constraint.team];
      if (value == null) {
        context.warnings.add("Some matching plays have no recorded rotation; those plays are excluded from rotation filters.");
        return null;
      }
      if (!constraint.positions.includes(String(value))) return false;
    } else {
      if (play.homeScore == null || play.awayScore == null) {
        context.warnings.add("Some matching plays have no recorded score; those plays are excluded from score-range filters.");
        return null;
      }
      value = constraint.type === "PR" ? Math.max(play.homeScore, play.awayScore)
        : query.team === "a" ? play.awayScore - play.homeScore : play.homeScore - play.awayScore;
      if ((constraint.min !== null && value < constraint.min) ||
        (constraint.max !== null && value > constraint.max)) return false;
    }
  }
  return true;
}

function matchesQuery(play, query, context, checkLimit, ignoreEffect = false) {
  checkLimit();
  if (query.operator === "missingPlayer") return false;
  if (query.operator === "or") return query.queries.some((child) => matchesQuery(play, child, context, checkLimit, ignoreEffect));
  if (query.operator === "and") return query.queries.every((child) => matchesQuery(play, child, context, checkLimit, ignoreEffect));
  let matches;
  if (!query.relation) {
    matches = matchesCode(play, query, ignoreEffect && !query.negate, context);
  } else if (query.relation.unit === "P") {
    const related = context.plays[play.index + query.relation.offset];
    matches = !!related && related.rally === play.rally && matchesCode(related, query, false, context);
  } else {
    const possession = query.relation.unit === "R" ? context.rallies.get(play.rally)
      : context.possessions[play.possession + query.relation.offset];
    matches = false;
    if (possession && possession.rally === play.rally) {
      if (!context.relationKeys.has(query)) context.relationKeys.set(query, JSON.stringify(query));
      const key = context.relationKeys.get(query);
      if (!context.relationCache.has(key)) context.relationCache.set(key, new Map());
      const results = context.relationCache.get(key);
      if (results.has(possession)) matches = results.get(possession);
      else {
        for (const related of possession.plays) {
          checkLimit();
          const result = matchesCode(related, query, false, context);
          if (result === true) {
            matches = true;
            break;
          }
          if (result === null) matches = null;
        }
        results.set(possession, matches);
      }
    }
  }
  if (matches === null) return false;
  return query.negate ? !matches : matches;
}

function createMatchContext(match, side, selectedGame) {
  const home = side === "away" ? match.teamB : match.teamA;
  const away = side === "away" ? match.teamA : match.teamB;
  const allSets = match.sets ?? match.drills ?? [];
  const sets = selectedGame ? [allSets[selectedGame - 1]] : allSets;
  if (sets.some((set) => !set)) throw new Error("The selected set does not exist.");
  const plays = [];
  const possessions = [];
  const rallies = new Map();
  let rally = 0;
  for (const set of sets) {
    const setRallies = [];
    rally += 1;
    let possession = null;
    let score = null;
    for (const event of set.events ?? []) {
      const skill = SKILLS[event.EventType];
      if (!skill) continue;
      const code = event.dvString?.split(";")[0] ?? "";
      const team = isHomeTeamEvent(event) === (side !== "away") ? "*" : "a";
      const currentScore = event.TeamScore != null && event.OppositionScore != null
        ? `${event.TeamScore}:${event.OppositionScore}` : null;
      if (skill === "S" || (score !== null && currentScore !== null && currentScore !== score)) {
        rally += 1;
        possession = null;
      }
      if (currentScore !== null) score = currentScore;
      const previous = possession?.plays[possession.plays.length - 1];
      if (!possession || possession.team !== team || ["S", "A"].includes(previous?.skill)) {
        possession = { team, rally, plays: [] };
        possessions.push(possession);
      }
      const play = {
        index: plays.length, rally, possession: possessions.length - 1,
        team, player: Number(event.Player?.shirtNumber ?? event.Player?.ShirtNumber),
        playerInfo: event.Player,
        event, match, set,
        skill, effect: event.DVGrade || code[5] || "",
      };
      const fields = event.dvString?.split(";") ?? [];
      const rotation = (value) => /^[1-6]$/.test(String(value)) ? Number(value) : null;
      const rotations = { "*": rotation(fields[9]), a: rotation(fields[10]) };
      const eventTeam = isHomeTeamEvent(event) ? "*" : "a";
      if (rotations[eventTeam] === null) {
        rotations[eventTeam] = rotation([0, 1, 6, 5, 4, 3, 2][event.Row]);
      }
      const scoreValue = (value) => value != null && value !== "" && Number.isFinite(Number(value))
        ? Number(value) : null;
      play.homeScore = scoreValue(side === "away" ? event.OppositionScore : event.TeamScore);
      play.awayScore = scoreValue(side === "away" ? event.TeamScore : event.OppositionScore);
      play.rotations = side === "away" ? { "*": rotations.a, a: rotations["*"] } : rotations;
      const combination = event.AdvanceCode1 || code.slice(6, 8);
      play.hitType = /^[A-Z]$/.test(code[4] ?? "") ? code[4] : null;
      const startZone = event.AdvanceCode3 || code[9];
      const endZone = event.AdvanceCode4 || code[10];
      play.startZone = /^[1-9]$/.test(startZone ?? "") ? String(startZone) : null;
      play.endZone = /^[1-9]$/.test(endZone ?? "") ? String(endZone) : null;
      if (skill === "E") {
        play.setterCall = /^[A-Z][A-Z0-9]$/.test(combination) ? combination : null;
        const target = event.AdvanceCode2 || code[8];
        play.targetAttacker = /^[BCFP]$/.test(target) ? target : null;
      } else if (skill === "A") {
        play.combination = /^[A-Z][A-Z0-9]$/.test(event.attackCombo || combination)
          ? event.attackCombo || combination : null;
      }
      possession.plays.push(play);
      plays.push(play);
      if (!rallies.has(rally)) {
        const entry = { rally, plays: [], winner: null };
        rallies.set(rally, entry);
        setRallies.push(entry);
      }
      const entry = rallies.get(rally);
      entry.plays.push(play);
      if (play.effect === "#" && ["S", "A", "B"].includes(skill)) entry.winner = team;
      else if (!entry.winner && (play.effect === "=" ||
        (play.effect === "/" && ["A", "B"].includes(skill)))) entry.winner = team === "*" ? "a" : "*";
    }
    const opening = setRallies[0]?.plays[0];
    const openingTotal = opening?.homeScore != null && opening?.awayScore != null
      ? opening.homeScore + opening.awayScore : null;
    for (let index = 0; index < setRallies.length; index += 1) {
      const entry = setRallies[index];
      if (entry.winner) continue;
      const first = entry.plays[0];
      const next = setRallies[index + 1]?.plays[0];
      const absoluteScores = openingTotal === 1;
      if (!absoluteScores && openingTotal !== 0) continue;
      const previous = setRallies[index - 1]?.plays[0];
      const baseHome = absoluteScores ? previous?.homeScore ?? 0 : first.homeScore;
      const baseAway = absoluteScores ? previous?.awayScore ?? 0 : first.awayScore;
      const endHome = absoluteScores ? first.homeScore
        : next?.homeScore ?? (side === "away" ? set.AwayScore : set.HomeScore);
      const endAway = absoluteScores ? first.awayScore
        : next?.awayScore ?? (side === "away" ? set.HomeScore : set.AwayScore);
      if (baseHome == null || baseAway == null || endHome == null || endAway == null) continue;
      if (endHome > baseHome && endAway === baseAway) entry.winner = "*";
      else if (endAway > baseAway && endHome === baseHome) entry.winner = "a";
    }
  }
  return { teams: { "*": home, a: away }, plays, possessions, rallies, warnings: new Set() };
}

function createWorkbookContext(matches, side, selectedGame, team) {
  if (!matches.length) throw new Error("Select at least one match to run a VolleyStation report.");
  if (matches.some((match) => match.app === "VBStats")) {
    throw new Error("VolleyStation templates require DataVolley or VolleyStation match files for every selected match.");
  }
  if (matches.length > 1 && !team) throw new Error("Select a common team for multi-match VolleyStation reports.");
  const contexts = matches.map((match) =>
    createMatchContext(match, getVSSMatchSide(match, team, side), selectedGame));
  if (contexts.length === 1) return contexts[0];
  const context = {
    teams: { "*": { players: [] }, a: { players: [] } },
    plays: [], possessions: [], rallies: new Map(), warnings: new Set(),
  };
  context.warnings.add("Multi-match player filters group plays by shirt number, not player identity. Changed shirt numbers remain separate; shared shirt numbers are combined.");
  for (const source of contexts) {
    const playOffset = context.plays.length;
    const possessionOffset = context.possessions.length;
    const rallyOffset = context.rallies.size;
    const rallyIds = new Map([...source.rallies.keys()].map((id, index) => [id, rallyOffset + index]));
    for (const play of source.plays) {
      play.index += playOffset;
      play.possession += possessionOffset;
      play.rally = rallyIds.get(play.rally);
    }
    for (const possession of source.possessions) possession.rally = rallyIds.get(possession.rally);
    for (const [id, rally] of source.rallies) {
      rally.rally = rallyIds.get(id);
      context.rallies.set(rally.rally, rally);
    }
    for (const play of source.plays) context.plays.push(play);
    for (const possession of source.possessions) context.possessions.push(possession);
    for (const warning of source.warnings) context.warnings.add(warning);
    for (const code of ["*", "a"]) {
      const players = context.teams[code].players;
      for (const player of source.teams[code]?.players ?? []) {
        const shirt = Number(player.shirtNumber ?? player.ShirtNumber);
        const existing = players.find((entry) => Number(entry.shirtNumber ?? entry.ShirtNumber) === shirt);
        if (!existing) players.push({ ...player });
        else {
          const identity = (entry) => [entry.FirstName, entry.LastName, entry.NickName].join("|");
          if (identity(existing) !== identity(player)) {
            existing.NickName = `#${shirt} (multiple players)`;
            existing.Positions = [];
            context.warnings.add("Some shirt numbers belong to different players across matches; their combined rows cannot be classified by position.");
          } else {
            existing.Positions = [...new Set([...(existing.Positions ?? []), ...(player.Positions ?? [])])];
          }
        }
      }
    }
  }
  return context;
}

function prepareSettingReport(workbook, context) {
  const summary = workbook.find((sheet) => sheet.name === "Sheet1" &&
    sheet.content.data.some((cell) => cell[0] === 2 && cell[1] === 4 &&
      typeof cell[2] === "string" && cell[2].includes('VSPLAYER("*1i","setter")')) &&
    sheet.content.data.some((cell) => cell[0] === 25 && cell[1] === 4 &&
      typeof cell[2] === "string" && cell[2].includes('VSPLAYER("*2i","setter")')));
  if (!summary) return workbook;
  const counts = new Map();
  const recorded = new Map();
  for (const play of context.plays) {
    if (play.team !== "*" || play.skill !== "E" || !Number.isInteger(play.player) || play.player < 1 || play.player > 99) continue;
    counts.set(play.player, (counts.get(play.player) ?? 0) + 1);
    if (play.playerInfo) recorded.set(play.player, play.playerInfo);
  }
  const players = [...(context.teams["*"]?.players ?? [])];
  context.teams["*"] = { ...context.teams["*"], players };
  const setters = new Map(players.filter((player) => player.Positions?.includes(POSITIONS.setter))
    .map((player) => [Number(player.shirtNumber ?? player.ShirtNumber), player]));
  for (const [shirt, player] of recorded) {
    if (!setters.has(shirt)) setters.set(shirt, players.find((entry) =>
      Number(entry.shirtNumber ?? entry.ShirtNumber) === shirt) ?? player);
  }
  const ranked = [...setters].filter(([shirt]) => Number.isInteger(shirt) && shirt > 0 && shirt <= 99)
    .sort(([left], [right]) => (counts.get(right) ?? 0) - (counts.get(left) ?? 0) || left - right);
  if (!ranked.length) {
    context.warnings.add("No setters with recorded setting plays or setter positions were found for the selected team.");
    return workbook;
  }
  if (ranked.length > 21) throw new Error("The Setting report supports at most 21 setter sections within its 500-row limit.");
  for (const [shirt, player] of ranked) {
    if (!players.some((entry) => Number(entry.shirtNumber ?? entry.ShirtNumber) === shirt)) players.push({ ...player });
  }
  context.setterPlayers = ranked.map(([, player]) => player);
  const shiftFormula = (value, offset) => {
    if (typeof value !== "string" || !value.startsWith("=")) return value;
    return value.split(/("(?:[^"]|"")*"|'(?:[^']|'')*')/).map((part, index) => index % 2 ? part :
      part.replace(/(\$?[A-Z]{1,3}\$?)([1-9]\d*)\b/g, (reference, column, row) => {
        const number = Number(row);
        return number >= 2 && number <= 24 ? `${column}${number + offset}` : reference;
      })).join("");
  };
  const block = summary.content.data.filter(([row]) => row >= 2 && row <= 24);
  const blockSpans = (summary.content.spans ?? []).filter((span) =>
    span[0] >= 2 && span[0] + span[3] - 1 <= 24);
  for (const column of [34, 39]) {
    if (!blockSpans.some(([row, start]) => row === 3 && start === column)) {
      blockSpans.push([3, column, 5, 1]);
    }
  }
  const data = summary.content.data.filter(([row]) => row === 1).map((cell) => [...cell]);
  const spans = [];
  const conditions = [];
  const sizes = (summary.content.sizes ?? []).filter(([row]) => row === 0 || row === 1).map((size) => [...size]);
  ranked.forEach(([shirt], index) => {
    const offset = index * 23;
    for (const cell of block) {
      const copy = [...cell];
      copy[0] += offset;
      copy[2] = cell[0] === 2 && cell[1] === 4 ? shirt : shiftFormula(cell[2], offset);
      if (cell[0] === 2 && cell[1] === 5) {
        copy[2] = `=VSPLAYERNAME("*"&D${2 + offset})`;
      }
      if (cell[0] === 2 && cell[1] >= 6 && cell[1] <= 8) copy[2] = "";
      data.push(copy);
    }
    for (const span of blockSpans) {
      spans.push([span[0] + offset, ...span.slice(1)]);
    }
    spans.push([2 + offset, 5, 4, 1]);
    for (const condition of summary.content.conditions ?? []) {
      if (condition[0] >= 2 && condition[0] <= 24) {
        conditions.push([condition[0] + offset, ...condition.slice(1)]);
      }
    }
    for (const size of summary.content.sizes ?? []) {
      if (size[0] >= 2 && size[0] <= 24) sizes.push([size[0] + offset, ...size.slice(1)]);
    }
  });
  context.warnings.add("Setting report: all setter-position players and players with recorded setting plays are shown in descending setting-play count, grouped by shirt number.");
  return workbook.map((sheet) => sheet === summary ? {
    ...sheet, content: { ...sheet.content, data, spans, conditions, sizes },
  } : sheet);
}

export function evaluateVSSWorkbook(workbook, match, { side = "home", selectedGame = 0, team, allSettingSetters = false } = {}) {
  const context = createWorkbookContext(Array.isArray(match) ? match : [match], side, selectedGame, team);
  if (allSettingSetters) workbook = prepareSettingReport(workbook, context);
  context.relationKeys = new WeakMap();
  context.relationCache = new Map();
  const diagnostics = [];
  const warnings = context.warnings;
  const sheetByName = new Map();
  let steps = 0;
  let depth = 0;
  const sheets = workbook.map((sheet) => {
    const result = {
      name: sheet.name, content: sheet.content, cells: new Map(), values: new Map(), eventSources: new Map(),
      rowCount: 1, columnCount: 1,
    };
    for (const [row, column, value, style, format] of sheet.content.data) {
      const address = `${vssColumnName(column)}${row}`;
      result.cells.set(address, { row, column, value, style, format });
      if (value !== "" && value != null) {
        result.rowCount = Math.max(result.rowCount, row);
        result.columnCount = Math.max(result.columnCount, column);
      }
    }
    for (const [row, column, width, height] of sheet.content.spans ?? []) {
      result.rowCount = Math.max(result.rowCount, row + height - 1);
      result.columnCount = Math.max(result.columnCount, column + width - 1);
    }
    sheetByName.set(sheet.name.toLowerCase(), result);
    return result;
  });
  const active = new Set();
  const cellStack = [];
  const playlistEvents = new WeakMap();
  const cache = new Map();
  const queryCache = new Map();
  const queryMembership = new WeakMap();
  const candidateCache = new Map();
  let playIndexes;

  function checkLimit() {
    steps += 1;
    if (steps > MAX_STEPS) fail("Workbook calculation limit exceeded.", "#LIMIT!");
  }

  function selectPlays(query, ignoreEffect = false) {
    if (ignoreEffect) {
      return selectPlays(withoutEffect(query));
    }
    const key = JSON.stringify(query);
    if (!queryCache.has(key)) {
      if (query.operator === "and") {
        const contextual = query.queries.filter(hasRelation);
        const selections = query.queries.filter((child) => !hasRelation(child)).map((child) => selectPlays(child));
        const smallest = selections.reduce((result, selection) =>
          selection.length < result.length ? selection : result, context.plays);
        const memberships = selections.filter((selection) => selection !== smallest).map((selection) => {
          if (!queryMembership.has(selection)) {
            const members = new Set();
            for (const play of selection) {
              checkLimit();
              members.add(play);
            }
            queryMembership.set(selection, members);
          }
          return queryMembership.get(selection);
        });
        queryCache.set(key, smallest.filter((play) => memberships.every((members) => {
          checkLimit();
          return members.has(play);
        }) && contextual.every((child) => matchesQuery(play, child, context, checkLimit))));
      } else if (query.operator === "or") {
        const union = new Set();
        for (const child of query.queries) {
          for (const play of selectPlays(child)) {
            checkLimit();
            union.add(play);
          }
        }
        queryCache.set(key, [...union].sort((left, right) => left.index - right.index));
      } else {
        const candidates = queryCandidates(query);
        queryCache.set(key, candidates.filter((play) => matchesQuery(play, query, context, checkLimit)));
      }
    }
    return queryCache.get(key);
  }

  function hasRelation(query) {
    return !!query.relation || !!query.queries?.some(hasRelation);
  }

  function withoutEffect(query) {
    if (query.operator === "or" || query.operator === "and") {
      return { ...query, queries: query.queries.map(withoutEffect) };
    }
    return query.operator === "code" && !query.relation && !query.negate
      ? { ...query, effect: null } : query;
  }

  function queryCandidates(query) {
    checkLimit();
    if (query.operator === "missingPlayer") return [];
    if (query.operator !== "code" || query.relation || query.negate) return context.plays;
    if (!playIndexes) {
      playIndexes = { team: new Map(), player: new Map(), skill: new Map(), effect: new Map() };
      for (const play of context.plays) {
        checkLimit();
        for (const [field, index] of Object.entries(playIndexes)) {
          if (!index.has(play[field])) index.set(play[field], []);
          index.get(play[field]).push(play);
        }
      }
    }
    let candidates = context.plays;
    const fields = [];
    for (const [field, value] of [
      ["team", query.team], ["player", query.shirt], ["skill", query.skill],
      ["combination", query.combination], ["hitType", query.hitType],
      ["startZone", query.startZone], ["endZone", query.endZone],
      ["effect", query.effect],
    ]) {
      if (value == null) continue;
      fields.push([field, value]);
      const key = JSON.stringify(fields);
      if (!candidateCache.has(key)) {
        const metadata = ["combination", "hitType", "startZone", "endZone"].includes(field);
        if (fields.length === 1 && !metadata) {
          candidateCache.set(key, playIndexes[field].get(value) ?? []);
        } else {
          candidateCache.set(key, candidates.filter((play) => {
            checkLimit();
            // Keep missing metadata so matchesCode can report exclusions.
            return play[field] === value || (metadata && !play[field]);
          }));
        }
      }
      candidates = candidateCache.get(key);
    }
    return candidates;
  }

  function cellValue(sheet, address) {
    const normalized = address.replace(/\$/g, "").toUpperCase();
    const key = `${sheet.name}!${normalized}`;
    if (cache.has(key)) {
      const value = cache.get(key);
      if (value instanceof FormulaError) throw value;
      inheritEvents(sheet, normalized);
      return value;
    }
    if (active.has(key)) fail("Circular cell reference.", "#CYCLE!");
    if (depth > 100) fail("Cell dependency chain is too deep.", "#REF!");
    const cell = sheet.cells.get(normalized);
    if (!cell) return "";
    active.add(key);
    cellStack.push({ sheet, address: normalized });
    depth += 1;
    try {
      const value = typeof cell.value === "string" && cell.value.startsWith("=")
        ? evaluateCellFormula(cell.value, sheet) : cell.value ?? "";
      if (typeof value === "number" && !Number.isFinite(value)) fail("Non-finite formula result.", "#NUM!");
      if (typeof value === "string" && value.length > 32767) fail("Cell text limit exceeded.", "#LIMIT!");
      if (value != null && typeof value === "object" &&
        (!Array.isArray(value) || value.some((entry) => entry != null && typeof entry === "object"))) {
        fail("A query or play object cannot be displayed directly; use VSCOUNT or VSMAP.");
      }
      cache.set(key, value);
      sheet.values.set(normalized, value);
      inheritEvents(sheet, normalized);
      return value;
    } catch (error) {
      if (!(error instanceof FormulaError)) throw error;
      cache.set(key, error);
      if (error instanceof NoDataRatioError) {
        sheet.values.set(normalized, "");
        warnings.add("No data: ratios with a zero denominator and formulas depending on them are shown blank, not as 0%.");
        throw error;
      }
      sheet.values.set(normalized, error.code);
      diagnostics.push({ sheet: sheet.name, cell: normalized, formula: cell.value, message: error.message, code: error.code });
      throw error;
    } finally {
      cellStack.pop();
      depth -= 1;
      active.delete(key);
    }
  }

  function inheritEvents(sheet, address) {
    const parent = cellStack[cellStack.length - 1];
    if (!parent || (parent.sheet === sheet && parent.address === address)) {
      const caller = cellStack[cellStack.length - 2];
      if (caller && sheet.eventSources.has(address) && !caller.sheet.eventSources.has(caller.address)) {
        caller.sheet.eventSources.set(caller.address, sheet.eventSources.get(address));
      }
      return;
    }
    if (sheet.eventSources.has(address) && !parent.sheet.eventSources.has(parent.address)) {
      parent.sheet.eventSources.set(parent.address, sheet.eventSources.get(address));
    }
  }

  function evaluateCellFormula(formula, sheet) {
    const legacyPlayer = /^=IF\(LEN\((VSPLAYER\("[^"]+"[,;]"[^"]+"\))\)=3[,;]RIGHT\(\1[,;]2\)[,;]RIGHT\(\1[,;]1\)\)$/i.exec(formula);
    if (legacyPlayer && evaluate(parseFormula(`=${legacyPlayer[1]}`), sheet) === "#!") {
      warnings.add("Compatibility: a legacy player-number formula references a missing position player; its slot is shown blank instead of using ! as a shirt number.");
      return "";
    }
    return evaluate(parseFormula(formula), sheet);
  }

  function referenceSheet(node, sheet) {
    if (!node.sheet) return sheet;
    const found = sheetByName.get(node.sheet.toLowerCase());
    if (!found) fail(`Unknown sheet: ${node.sheet}.`, "#REF!");
    return found;
  }

  function evaluate(node, sheet) {
    steps += 1;
    if (steps > MAX_STEPS) fail("Workbook calculation limit exceeded.", "#LIMIT!");
    if (node.type === "literal") return node.value;
    if (node.type === "reference") return cellValue(referenceSheet(node, sheet), node.reference);
    if (node.type === "range") {
      const source = referenceSheet(node.start, sheet);
      const start = coordinates(node.start.reference);
      const values = [];
      const events = new Set();
      for (let row = Math.min(start.row, node.end.row); row <= Math.max(start.row, node.end.row); row += 1) {
        for (let column = Math.min(start.column, node.end.column); column <= Math.max(start.column, node.end.column); column += 1) {
          steps += 1;
          if (steps > MAX_STEPS) fail("Workbook calculation limit exceeded.", "#LIMIT!");
          values.push(cellValue(source, `${vssColumnName(column)}${row}`));
          for (const event of source.eventSources.get(`${vssColumnName(column)}${row}`) ?? []) events.add(event);
        }
      }
      const current = cellStack[cellStack.length - 1];
      if (events.size) current.sheet.eventSources.set(current.address, [...events]);
      return values;
    }
    if (node.type === "unary") return (node.operator === "-" ? -1 : 1) * number(evaluate(node.value, sheet));
    if (node.type === "binary") {
      if (!["+", "-"].includes(node.operator)) {
        return binary(node.operator, evaluate(node.left, sheet), evaluate(node.right, sheet));
      }
      const current = cellStack[cellStack.length - 1];
      const previous = current.sheet.eventSources.get(current.address);
      current.sheet.eventSources.delete(current.address);
      const left = evaluate(node.left, sheet);
      const leftEvents = current.sheet.eventSources.get(current.address);
      current.sheet.eventSources.delete(current.address);
      const right = evaluate(node.right, sheet);
      const rightEvents = current.sheet.eventSources.get(current.address);
      if (previous || leftEvents || rightEvents) {
        current.sheet.eventSources.set(current.address, [...new Set([
          ...(previous ?? []), ...(leftEvents ?? []), ...(rightEvents ?? []),
        ])]);
      }
      return binary(node.operator, left, right);
    }
    const { name, args } = node;
    const arg = (n) => args[n] ? evaluate(args[n], sheet) : "";
    if (name === "IF") {
      if (args.length < 2 || args.length > 3) fail("IF requires two or three arguments.");
      // VSS/Webix sheets use a blank omitted branch for empty roster rows.
      return arg(0) ? arg(1) : args[2] ? arg(2) : "";
    }
    if (name === "IFERROR") {
      if (args.length !== 2) fail("IFERROR requires two arguments.");
      try { return arg(0); } catch (error) {
        if (!(error instanceof FormulaError)) throw error;
        return arg(1);
      }
    }
    const sumEvents = new Set();
    const values = args.map((item) => {
      const current = cellStack[cellStack.length - 1];
      if (name === "SUM") current.sheet.eventSources.delete(current.address);
      const value = evaluate(item, sheet);
      if (name === "SUM") {
        for (const event of current.sheet.eventSources.get(current.address) ?? []) sumEvents.add(event);
      }
      return value;
    });
    if (name === "SUM" && sumEvents.size) {
      const current = cellStack[cellStack.length - 1];
      current.sheet.eventSources.set(current.address, [...sumEvents]);
    }
    if (name.startsWith("VS")) return volleyFunction(name, values);
    const flattened = values.flat(Infinity);
    const numeric = flattened.filter((value) => typeof value === "number");
    switch (name) {
      case "SUM": return numeric.reduce((sum, value) => sum + value, 0);
      case "COUNT": return numeric.length;
      case "COUNTA": return flattened.filter((value) => value !== "" && value != null).length;
      case "AVERAGE": if (!numeric.length) fail("AVERAGE has no numbers.", "#DIV/0!"); return numeric.reduce((a, b) => a + b, 0) / numeric.length;
      case "MIN": return numeric.length ? Math.min(...numeric) : 0;
      case "MAX": return numeric.length ? Math.max(...numeric) : 0;
      case "ABS": return Math.abs(number(values[0]));
      case "ROUND":
      case "ROUNDUP": {
        const digits = number(values[1] ?? 0);
        if (!Number.isInteger(digits) || Math.abs(digits) > 15) fail("Unsupported ROUND precision.");
        const value = number(values[0]);
        const factor = 10 ** digits;
        return Math.sign(value) * (name === "ROUNDUP"
          ? Math.ceil(Number((Math.abs(value) * factor).toPrecision(15)))
          : Math.round((Math.abs(value) + Number.EPSILON) * factor)) / factor;
      }
      case "LEN": return text(values[0]).length;
      case "LEFT":
      case "RIGHT": {
        const count = number(values[1] ?? 1);
        if (count < 0 || !Number.isInteger(count)) fail(`${name} requires a non-negative character count.`);
        const value = text(values[0]);
        return name === "LEFT" ? value.slice(0, count) : count ? value.slice(-count) : "";
      }
      case "CONCATENATE": return flattened.reduce((value, item) => binary("&", value, item), "");
      case "AND": return flattened.every(Boolean);
      case "OR": return flattened.some(Boolean);
      case "NOT": return !values[0];
      default: return fail(`Unsupported function: ${name}.`, "#NAME?");
    }
  }

  function volleyFunction(name, values) {
    if (name === "VSPLAYER") {
      const selector = /^([*a])([1-9]\d*)(i)?$/.exec(text(values[0]));
      if (!selector) fail("VSPLAYER supports team/number selectors, for example *1i or a7.");
      let players = context.teams[selector[1]]?.players ?? [];
      if (values[1]) {
        const position = POSITIONS[text(values[1]).toLowerCase()];
        if (!position) fail(`Unsupported player position: ${text(values[1])}.`);
        if (position === POSITIONS.setter && selector[1] === "*" && context.setterPlayers) {
          players = context.setterPlayers;
        } else {
          if (players.some((player) => !player.Positions?.length)) {
            warnings.add("Some players have no recorded position; position-filtered formulas cannot include them.");
          }
          players = players.filter((player) => player.Positions?.includes(position));
        }
      }
      const player = selector[3] ? players[Number(selector[2]) - 1] :
        players.find((p) => Number(p.shirtNumber ?? p.ShirtNumber) === Number(selector[2]));
      return player ? `${selector[1]}${Number(player.shirtNumber ?? player.ShirtNumber)}` : "#!";
    }
    if (name === "VSPLAYERNAME") {
      const source = text(values[0]);
      if (["", "#!", "*", "a"].includes(source)) return "";
      const selector = /^([*a])(\d{1,2})$/.exec(source);
      if (!selector) fail("VSPLAYERNAME supports a team/shirt selector, for example *7.");
      const player = context.teams[selector[1]]?.players?.find(
        (p) => Number(p.shirtNumber ?? p.ShirtNumber) === Number(selector[2])
      );
      return player?.NickName || player?.LastName || player?.FirstName || "";
    }
    if (name === "VSCELL") {
      if (values.slice(1).includes("#!")) {
        warnings.add("Some player selectors reference missing roster entries; their filters match no plays.");
        return { kind: "query", operator: "missingPlayer" };
      }
      const source = text(values[0]).replace(/\((\d+)\)/g, (_, index) => {
        if (Number(index) >= values.length) fail(`Missing VSCELL substitution (${index}).`);
        return text(values[Number(index)]);
      });
      return simpleQuery(source);
    }
    if (name === "VSOR" || name === "VSAND") {
      if (!values.length) fail(`${name} needs at least one filter.`);
      return { kind: "query", operator: name === "VSOR" ? "or" : "and", queries: values.map(simpleQuery) };
    }
    if (name === "VSRANGE") {
      const range = /^([1-9]\d*):([1-9]\d*)$/.exec(text(values[0]));
      if (!range || Number(range[1]) > Number(range[2])) fail("VSRANGE supports positive play-index ranges such as 1:30.");
      return { kind: "playRange", start: Number(range[1]), end: Number(range[2]) };
    }
    if (name === "VSMAP") {
      if (!Array.isArray(values[0]) || values[1] !== "effect") {
        fail('VSMAP currently supports mapping VSPLAYS results to "effect" only.');
      }
      return values[0].map((play) => play.effect).join(", ");
    }
    const filters = (name === "VSPLAYS" ? values.slice(0, 1) : values).map(simpleQuery);
    const query = filters.length === 1 ? filters[0] : { kind: "query", operator: "and", queries: filters };
    const plays = selectPlays(query);
    const current = cellStack[cellStack.length - 1];
    if (current && ["distribution", "sheet1", "pts vs err", "positions", "rotation"].includes(current.sheet.name.toLowerCase()) &&
      !current.sheet.eventSources.has(current.address) && ["VSCOUNT", "VSPER", "VSEFF"].includes(name)) {
      const selected = current.sheet.name.toLowerCase() === "distribution"
        ? plays.filter((play) => play.skill === "E") : plays;
      current.sheet.eventSources.set(current.address, selected.map((play) => {
        if (!playlistEvents.has(play)) {
          playlistEvents.set(play, { ...play.event, Drill: { ...play.set, match: play.match } });
        }
        return playlistEvents.get(play);
      }));
    }
    if (name === "VSCOUNT") {
      return plays.length;
    }
    if (name === "VSPLAYS") {
      if (values.length === 1) return plays;
      if (values[1]?.kind !== "playRange") fail("VSPLAYS supports a filter and an optional VSRANGE.");
      return plays.slice(values[1].start - 1, values[1].end);
    }
    if (name === "VSPER") {
      const total = selectPlays(query, true).length;
      return total ? plays.length / total : 0;
    }
    if (name === "VSEFF") {
      if (plays.some((play) => play.skill !== "A")) fail("VSEFF is currently supported for attack filters only.");
      if (!plays.length) return 0;
      return plays.reduce((sum, play) => sum + (play.effect === "#" ? 1 : ["=", "/"].includes(play.effect) ? -1 : 0), 0) / plays.length;
    }
    return fail(`Unsupported function: ${name}.`, "#NAME?");
  }

  for (const sheet of sheets) {
    for (const address of sheet.cells.keys()) {
      try { cellValue(sheet, address); } catch (error) {
        if (!(error instanceof FormulaError)) throw error;
      }
    }
    if (sheet.content.views?.length) {
      warnings.add("Embedded VSS charts and drawings are not rendered; their source cells are calculated below.");
    }
    if (sheet.content.formats?.length || sheet.content.ranges?.length) {
      warnings.add("Custom number formats and named ranges are not supported.");
    }
  }
  return { sheets, diagnostics, warnings: [...warnings] };
}

export function formatVSSValue(value, cell, content) {
  if (typeof value === "number") {
    const style = (content.styles ?? []).find((entry) => entry[0] === cell?.style)?.[1];
    if (typeof style === "string" && style.split(";")[11] === "percent") {
      return `${Number((value * 100).toFixed(2))}%`;
    }
    return String(Number(value.toFixed(8)));
  }
  if (Array.isArray(value)) return value.map((item) => text(item)).join(", ");
  if (typeof value === "object" && value != null) return "#VALUE!";
  return value == null ? "" : String(value);
}
